// frontend/src/api/index.js
 export const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (typeof window !== 'undefined' && window.location.hostname !== 'localhost'
    ? '/api'
    : 'http://localhost:5000/api');

async function request(path, { method = 'GET', signal, body, headers } = {}) {
  try {
    // add cache-busting param
    const ts = Date.now();
    const sep = path.includes('?') ? '&' : '?';
    const finalPath = `${path}${sep}__ts=${ts}`;

    // Get access token from localStorage
    const accessToken = localStorage.getItem('accessToken');
    
    const res = await fetch(`${API_BASE}${finalPath}`, {
      method,
      signal,
      body,
      headers: { 
        'Cache-Control': 'no-cache', 
        'Pragma': 'no-cache',
        ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
        ...(headers || {}) 
      },
      cache: 'no-store'
    });
    const contentType = res.headers.get('content-type') || '';
    const data = contentType.includes('application/json') ? await res.json().catch(() => ({})) : await res.text();
    if (!res.ok) {
      // If response has validationErrors, return the data instead of throwing
      if (typeof data === 'object' && data?.validationErrors) {
        return data;
      }
      const msg = (typeof data === 'object' && (data?.message || data?.error)) || `Request failed: ${res.status}`;
      throw new Error(msg);
    }
    return data;
  } catch (error) {
    // Silently handle AbortError - it's expected when requests are cancelled
    if (error.name === 'AbortError' || error.message?.includes('aborted')) {
      console.log('[API] Request aborted (expected):', path);
      // Create a custom error that won't trigger React error overlay
      const abortError = new Error('Request was cancelled');
      abortError.name = 'AbortError';
      abortError.silent = true; // Mark as silent for error handlers
      throw abortError;
    }
    
    // Handle network errors (Failed to fetch)
    if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
      console.error('[API] Network error - is the backend server running?', path);
      const networkError = new Error('Network error - please check your connection');
      networkError.name = 'NetworkError';
      throw networkError;
    }
    
    // Re-throw other errors
    throw error;
  }
}

export async function requestJson(path, { method = 'GET', signal, json } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  const body = json !== undefined ? JSON.stringify(json) : undefined;
  return request(path, { method, signal, headers, body });
}

// GET /api/applications/:applicationId/details
export function getApplicationDetails(applicationId, { signal } = {}) {
  return request(`/applications/${encodeURIComponent(applicationId)}/details`, { signal });
}

// GET /api/applications/:applicationId/individuals
export function getApplicationIndividuals(applicationId, { signal } = {}) {
  return request(`/applications/${encodeURIComponent(applicationId)}/individuals`, { signal });
}



// GET /api/getallapplications
// export function getAllApplications({ page = 1, limit = 10, status, loanType, search } = {}, { signal } = {}) {
//   const params = new URLSearchParams();
//   params.set('page', String(page));
//   params.set('limit', String(limit));
//   if (status) params.set('status', status);
//   if (loanType) params.set('loanType', loanType);
//   if (search) params.set('search', search);
//   return request(`/getallapplications?${params.toString()}`, { signal });
// }


export function getAllApplications({ page = 1, limit = 10, status, loanType, search, startDate, endDate } = {}, { signal } = {}) {
  console.log('[API] getAllApplications called with params:', { page, limit, status, loanType, search, startDate, endDate });
  
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('limit', String(limit));
  if (status) params.set('status', status);
  if (loanType) params.set('loanType', loanType);
  if (search) params.set('search', search);
  
  // Enhanced date parameter logging
  if (startDate) {
    console.log('[API] Setting startDate parameter:', startDate);
    params.set('startDate', startDate);
  }
  if (endDate) {
    console.log('[API] Setting endDate parameter:', endDate);
    params.set('endDate', endDate);
  }
  
  const url = `/dashboard/getallapplications?${params.toString()}`;
  console.log('[API] Final URL:', url);
  
  return request(url, { signal });
}
 
 
// Add this export to your existing api/index.js file
export function getDropdownOptions({ signal } = {}) {
  return request('/dashboard/dropdown-options', { signal });
}
 




// GET /api/simple-applications - Fetch only applicationId and uploadDate from Azure blob
export function getSimpleApplications({ signal } = {}) {
  return request('/simple-applications', { signal });
}

// POST /api/upload-folder
export function uploadFolder(formData, { signal } = {}) {
  // formData must contain one or more 'files' entries and optional folderName
  return request(`/upload-folder`, { method: 'POST', body: formData, signal });
}

// GET /api/applications/:applicationId/blob-info - Fetch application-info.json from Azure Blob
export function getApplicationBlobInfo(applicationId, { signal } = {}) {
  return request(`/applications/${encodeURIComponent(applicationId)}/blob-info`, { signal });
}

// GET /
export function getRoot({ signal } = {}) {
  return request(`/`, { signal });
}

// GET /api/test
export function testAzure({ signal } = {}) {
  return request(`/test`, { signal });
}

// GET /api/test/database
export function testDatabase({ signal } = {}) {
  return request(`/test/database`, { signal });
}

// GET /api/folder/:folderName
export function getFolder(folderName, { signal } = {}) {
  return request(`/folder/${encodeURIComponent(folderName)}`, { signal });
}

// GET /api/folders
export function getFolders({ signal } = {}) {
  return request(`/folders`, { signal });
}

// POST /api/applications/:applicationId/verification/increment
export function incrementVerification(applicationId, { signal } = {}) {
  return request(`/applications/${encodeURIComponent(applicationId)}/verification/increment`, {
    method: 'POST',
    signal
  });
}

// DELETE /api/folder/:folderName
export function deleteFolder(folderName, { signal } = {}) {
  return request(`/folder/${encodeURIComponent(folderName)}`, { method: 'DELETE', signal });
}

// GET /api/applications/search?q=...&type=...
export function searchApplications({ q, type }, { signal } = {}) {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (type) params.set('type', type);
  return request(`/applications/search?${params.toString()}`, { signal });
}

// GET /api/applications/filters
export function getApplicationFilters({ signal } = {}) {
  return request(`/applications/filters`, { signal });
}

// GET /api/applications/statistics?period=30
export function getApplicationStatistics({ period = '30' } = {}, { signal } = {}) {
  const params = new URLSearchParams();
  if (period) params.set('period', String(period));
  return request(`/applications/statistics?${params.toString()}`, { signal });
}

// GET /api/applications/:applicationId/company
export function getApplicationCompany(applicationId, { signal } = {}) {
  return request(`/applications/${encodeURIComponent(applicationId)}/company`, { signal });
}

// GET /api/silver/loanapplications/:applicationId
export function getSilverLoanApplication(applicationId, { signal } = {}) {
  return request(`/silver/loanapplications/${encodeURIComponent(applicationId)}`, { signal });
}

// URL for streaming the PDF of a Silver loan application
export function getSilverPdfUrl(applicationId) {
  return `${API_BASE}/silver/loanapplications/${encodeURIComponent(applicationId)}/pdf`;
}

// PUT /api/silver/loanapplications/:applicationId
export function updateSilverLoanApplication(applicationId, payload, { signal } = {}) {
  console.log('🔵 [API] updateSilverLoanApplication called');
  console.log('  - ApplicationID:', applicationId);
  console.log('  - Payload:', JSON.stringify(payload, null, 2));
  console.log('  - URL:', `/silver/loanapplications/${encodeURIComponent(applicationId)}`);
  
  return requestJson(`/silver/loanapplications/${encodeURIComponent(applicationId)}`, {
    method: 'PUT',
    signal,
    json: payload || {}
  }).then(response => {
    console.log('✅ [API] updateSilverLoanApplication response:', JSON.stringify(response, null, 2));
    return response;
  }).catch(error => {
    console.error('❌ [API] updateSilverLoanApplication error:', error);
    throw error;
  });
}

// GET /api/silver/adharapplications/:applicationId
export function getAadhaarApplication(applicationId, { signal, role } = {}) {
  const q = role ? `?role=${encodeURIComponent(role)}` : '';
  return request(`/silver/adharapplications/${encodeURIComponent(applicationId)}${q}`, { signal });
}

// URL for streaming the PDF of an Aadhaar schema record
export function getAadhaarPdfUrl(applicationId, role) {
  const q = role ? `?role=${encodeURIComponent(role)}` : '';
  return `${API_BASE}/silver/adharapplications/${encodeURIComponent(applicationId)}/pdf${q}`;
}

// PUT /api/silver/adharapplications/:applicationId
export function updateAadhaarApplication(applicationId, payload, { signal, role } = {}) {
  const q = role ? `?role=${encodeURIComponent(role)}` : '';
  return requestJson(`/silver/adharapplications/${encodeURIComponent(applicationId)}${q}`, {
    method: 'PUT',
    signal,
    json: payload || {}
  });
}

// GET /api/silver/panapplications/:applicationId
export function getPanApplication(applicationId, { signal, role } = {}) {
  const q = role ? `?role=${encodeURIComponent(role)}` : '';
  return request(`/silver/panapplications/${encodeURIComponent(applicationId)}${q}`, { signal });
}

// URL for streaming the PDF of a PAN record
export function getPanPdfUrl(applicationId, role) {
  const q = role ? `?role=${encodeURIComponent(role)}` : '';
  return `${API_BASE}/silver/panapplications/${encodeURIComponent(applicationId)}/pdf${q}`;
}

// PUT /api/silver/panapplications/:applicationId
export function updatePanApplication(applicationId, payload, { signal, role } = {}) {
  const q = role ? `?role=${encodeURIComponent(role)}` : '';
  return requestJson(`/silver/panapplications/${encodeURIComponent(applicationId)}${q}`, {
    method: 'PUT',
    signal,
    json: payload || {}
  });
}

// GET /api/silver/itrapplications/:applicationId
export function getItrApplication(applicationId, { signal, role, year } = {}) {
  const params = [];
  if (role) params.push(`role=${encodeURIComponent(role)}`);
  if (year) params.push(`year=${encodeURIComponent(year)}`);
  const q = params.length ? `?${params.join('&')}` : '';
  return request(`/silver/itrapplications/${encodeURIComponent(applicationId)}${q}`, { signal });
}

// URL for streaming the PDF of an ITR record
export function getItrPdfUrl(applicationId, role, year) {
  const params = [];
  if (role) params.push(`role=${encodeURIComponent(role)}`);
  if (year) params.push(`year=${encodeURIComponent(year)}`);
  const q = params.length ? `?${params.join('&')}` : '';
  return `${API_BASE}/silver/itrapplications/${encodeURIComponent(applicationId)}/pdf${q}`;
}

// PUT /api/silver/itrapplications/:applicationId
export function updateItrApplication(applicationId, payload, { signal, role, year } = {}) {
  const params = [];
  if (role) params.push(`role=${encodeURIComponent(role)}`);
  if (year) params.push(`year=${encodeURIComponent(year)}`);
  const q = params.length ? `?${params.join('&')}` : '';
  return requestJson(`/silver/itrapplications/${encodeURIComponent(applicationId)}${q}`, {
    method: 'PUT',
    signal,
    json: payload || {}
  });
}

// GET /api/silver/networth/:applicationId
export function getNetWorth(applicationId, { signal, role } = {}) {
  const q = role ? `?role=${encodeURIComponent(role)}` : '';
  return request(`/silver/networth/${encodeURIComponent(applicationId)}${q}`, { signal });
}

// URL for streaming the PDF of a Net Worth record
export function getNetWorthPdfUrl(applicationId, role) {
  const q = role ? `?role=${encodeURIComponent(role)}` : '';
  return `${API_BASE}/silver/networth/${encodeURIComponent(applicationId)}/pdf${q}`;
}

// PUT /api/silver/networth/:applicationId
export function updateNetWorth(applicationId, payload, { signal, role, id } = {}) {
  const params = [];
  if (role) params.push(`role=${encodeURIComponent(role)}`);
  if (id != null) params.push(`id=${encodeURIComponent(id)}`);
  const q = params.length ? `?${params.join('&')}` : '';
  return requestJson(`/silver/networth/${encodeURIComponent(applicationId)}${q}`, {
    method: 'PUT',
    signal,
    json: payload || {}
  });
}

// GET /api/silver/cibil/:applicationId
export function getCibil(applicationId, { signal, role } = {}) {
  const q = role ? `?role=${encodeURIComponent(role)}` : '';
  return request(`/silver/cibil/${encodeURIComponent(applicationId)}${q}`, { signal });
}

// URL for streaming the PDF of a CIBIL record
export function getCibilPdfUrl(applicationId, role) {
  const q = role ? `?role=${encodeURIComponent(role)}` : '';
  return `${API_BASE}/silver/cibil/${encodeURIComponent(applicationId)}/pdf${q}`;
}

// PUT /api/silver/cibil/:applicationId
export function updateCibil(applicationId, payload, { signal, role } = {}) {
  console.log('🔵 [API] updateCibil called with:', { applicationId, payload, role });
  const q = role ? `?role=${encodeURIComponent(role)}` : '';
  const result = requestJson(`/silver/cibil/${encodeURIComponent(applicationId)}${q}`, {
    method: 'PUT',
    signal,
    json: payload || {}
  });
  console.log('🔵 [API] updateCibil request sent');
  return result;
}

// GET /api/silver/balancesheet/:applicationId
export function getBalanceSheet(applicationId, { signal, year } = {}) {
  const q = year ? `?year=${encodeURIComponent(year)}` : '';
  return request(`/silver/balancesheet/${encodeURIComponent(applicationId)}${q}`, { signal });
}

// URL for streaming the PDF of a Balance Sheet record
export function getBalanceSheetPdfUrl(applicationId, year) {
  const q = year ? `?year=${encodeURIComponent(year)}` : '';
  return `${API_BASE}/silver/balancesheet/${encodeURIComponent(applicationId)}/pdf${q}`;
}

// PUT /api/silver/balancesheet/:applicationId
export function updateBalanceSheet(applicationId, payload, { signal, year } = {}) {
  const q = year ? `?year=${encodeURIComponent(year)}` : '';
  return requestJson(`/silver/balancesheet/${encodeURIComponent(applicationId)}${q}`, {
    method: 'PUT',
    signal,
    json: payload || {}
  });
}

// GET /api/silver/bankstat/:applicationId
export function getBankStat(applicationId, { signal } = {}) {
  return request(`/silver/bankstat/${encodeURIComponent(applicationId)}`, { signal });
}

// URL for streaming Bank Stat PDF
export function getBankStatPdfUrl(applicationId) {
  return `${API_BASE}/silver/bankstat/${encodeURIComponent(applicationId)}/pdf`;
}

// PUT /api/silver/bankstat/:applicationId
export function updateBankStat(applicationId, payload, { signal } = {}) {
  return requestJson(`/silver/bankstat/${encodeURIComponent(applicationId)}`, {
    method: 'PUT',
    signal,
    json: payload || {}
  });
}

// GET /api/silver/bankstat-summary/:applicationId
export function getBankStatSummary(applicationId, { signal } = {}) {
  return request(`/silver/bankstat-summary/${encodeURIComponent(applicationId)}`, { signal });
}

// URL for streaming Bank Stat Summary PDF
export function getBankStatSummaryPdfUrl(applicationId) {
  return `${API_BASE}/silver/bankstat-summary/${encodeURIComponent(applicationId)}/pdf`;
}

// PUT /api/silver/bankstat-summary/:applicationId
export function updateBankStatSummary(applicationId, payload, { signal } = {}) {
  return requestJson(`/silver/bankstat-summary/${encodeURIComponent(applicationId)}`, {
    method: 'PUT',
    signal,
    json: payload || {}
  });
}

// GET /api/silver/company-cibil/:applicationId
export function getCompanyCibil(applicationId, { signal } = {}) {
  return request(`/silver/company-cibil/${encodeURIComponent(applicationId)}`, { signal });
}

// URL for streaming Company CIBIL PDF
export function getCompanyCibilPdfUrl(applicationId) {
  return `${API_BASE}/silver/company-cibil/${encodeURIComponent(applicationId)}/pdf`;
}

// PUT /api/silver/company-cibil/:applicationId
export function updateCompanyCibil(applicationId, payload, { signal } = {}) {
  console.log('🔵 [API] updateCompanyCibil called with:', { applicationId, payload });
  const result = requestJson(`/silver/company-cibil/${encodeURIComponent(applicationId)}`, {
    method: 'PUT',
    signal,
    json: payload || {}
  });
  console.log('🔵 [API] updateCompanyCibil request sent');
  return result;
}

// GET /api/silver/gst/:applicationId
export function getGst(applicationId, { signal } = {}) {
  return request(`/silver/gst/${encodeURIComponent(applicationId)}`, { signal });
}

// URL for streaming GST PDF
export function getGstPdfUrl(applicationId) {
  return `${API_BASE}/silver/gst/${encodeURIComponent(applicationId)}/pdf`;
}

// PUT /api/silver/gst/:applicationId
export function updateGst(applicationId, payload, { signal } = {}) {
  console.log('🔵 [API] updateGst called with:', { applicationId, payload });
  const result = requestJson(`/silver/gst/${encodeURIComponent(applicationId)}`, {
    method: 'PUT',
    signal,
    json: payload || {}
  });
  console.log('🔵 [API] updateGst request sent');
  return result;
}

// GET /api/silver/profit-loss/:applicationId
// export function getProfitLoss(applicationId, opts = {}) {
//   const { signal, year } = opts || {};
//   const url = year
//     ? `/silver/profit-loss/${encodeURIComponent(applicationId)}?year=${encodeURIComponent(year)}`
//     : `/silver/profit-loss/${encodeURIComponent(applicationId)}`;
//   return request(url, { signal });
// }


// GET /api/silver/profit-loss/:applicationId
export function getProfitLoss(applicationId, opts = {}) {
  const { signal, year } = opts || {};
  
  console.log('📞 [API] getProfitLoss called:', { applicationId, year });
  
  // Build URL with year parameter - ensure year is always included if provided
  let url = `/silver/profit-loss/${encodeURIComponent(applicationId)}`;
  
  if (year) {
    // Ensure year is a clean string
    const cleanYear = String(year).trim();
    url += `?year=${encodeURIComponent(cleanYear)}`;
    console.log(' [API] getProfitLoss URL with year:', url);
  } else {
    console.log(' [API] getProfitLoss URL without year:', url);
  }
  
  return request(url, { signal }).then(response => {
    console.log('📞 [API] getProfitLoss response:', {
      applicationId,
      year,
      receivedData: {
        ID: response?.data?.ID,
        Assessment_Year: response?.data?.Assessment_Year,
        Fiscal_Year: response?.data?.Fiscal_Year,
        Sales: response?.data?.Sales,
        Net_Profit: response?.data?.Net_Profit
      }
    });
    return response;
  }).catch(error => {
    console.error('📞 [API] getProfitLoss error:', { applicationId, year, error: error.message });
    throw error;
  });
}


// URL for streaming Profit & Loss PDF
export function getProfitLossPdfUrl(applicationId, opts = {}) {
  const { year } = opts || {};
  const url = year
    ? `${API_BASE}/silver/profit-loss/${encodeURIComponent(applicationId)}/pdf?year=${encodeURIComponent(year)}`
    : `${API_BASE}/silver/profit-loss/${encodeURIComponent(applicationId)}/pdf`;
  return url;
}

// PUT /api/silver/profit-loss/:applicationId
export function updateProfitLoss(applicationId, payload, opts = {}) {
  const { signal, year, id } = opts || {};
  let query = '';
  if (year) query += `${query ? '&' : '?'}year=${encodeURIComponent(year)}`;
  if (id != null) query += `${query ? '&' : '?'}id=${encodeURIComponent(id)}`;
  const path = `/silver/profit-loss/${encodeURIComponent(applicationId)}${query}`;
  return requestJson(path, {
    method: 'PUT',
    signal,
    json: payload || {}
  });
}

// GET /api/silver/udyam/:applicationId
export function getUdyam(applicationId, { signal } = {}) {
  return request(`/silver/udyam/${encodeURIComponent(applicationId)}`, { signal });
}

// URL for streaming UDYAM PDF
export function getUdyamPdfUrl(applicationId) {
  return `${API_BASE}/silver/udyam/${encodeURIComponent(applicationId)}/pdf`;
}

// PUT /api/silver/udyam/:applicationId
export function updateUdyam(applicationId, payload, { signal } = {}) {
  return requestJson(`/silver/udyam/${encodeURIComponent(applicationId)}`, {
    method: 'PUT',
    signal,
    json: payload || {}
  });
}
